/**
 * Upload adapter — Shopify CDN (file properties) ou Cloudinary (URL em properties).
 * Uma plataforma por instância; sem fallbacks encadeados.
 */
(function (global) {
  'use strict';

  const PROVIDERS = {
    SHOPIFY: 'shopify',
    CLOUDINARY: 'cloudinary',
  };

  const getCartAddUrl = () =>
    global.Theme?.routes?.cart_add_url || '/cart/add.js';

  const getCartUpdateUrl = () =>
    global.Theme?.routes?.cart_update_url || '/cart/update.js';

  /**
   * Atualiza atributos do carrinho (pedido inteiro).
   * @param {Record<string, string>} attributes
   */
  async function updateCartAttributes(attributes) {
    const response = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });
    return response.json();
  }

  /**
   * @param {{ cloudName: string, uploadPreset: string, folder?: string }} config
   */
  function createCloudinaryUploader(config) {
    const cloudName = config.cloudName || '';
    const uploadPreset = config.uploadPreset || '';
    const folder = config.folder || 'grupo-mc/personalization';

    if (!cloudName || !uploadPreset) {
      throw new Error('Cloudinary: cloud_name e upload_preset são obrigatórios');
    }

    const uploadBlob = async (blob, prefix) => {
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('upload_preset', uploadPreset);
      formData.append('public_id', `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
      formData.append('folder', folder);
      formData.append('tags', 'grupo-mc,personalization');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Upload Cloudinary falhou: ${response.status} ${errText}`);
      }

      const result = await response.json();
      return result.secure_url;
    };

    return {
      provider: PROVIDERS.CLOUDINARY,

      async uploadLogo(file) {
        return uploadBlob(file, 'logo');
      },

      async uploadMockup(blob) {
        return uploadBlob(blob, 'mockup');
      },

      async addToCart({ variantId, quantity, properties, sectionIds }) {
        const body = {
          id: parseInt(variantId, 10),
          quantity: parseInt(quantity, 10) || 1,
          properties,
        };

        if (sectionIds?.length) {
          body.sections = sectionIds.join(',');
        }

        const response = await fetch(getCartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });

        return response.json();
      },

      updateCartAttributes,
    };
  }

  function createShopifyUploader() {
    return {
      provider: PROVIDERS.SHOPIFY,

      async uploadLogo() {
        return null;
      },

      async uploadMockup() {
        return null;
      },

      async addToCart({ variantId, quantity, properties, files = {}, sectionIds }) {
        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', String(quantity));

        for (const [key, value] of Object.entries(properties)) {
          if (value != null && value !== '') {
            formData.append(`properties[${key}]`, String(value));
          }
        }

        for (const [key, file] of Object.entries(files)) {
          if (file instanceof Blob) {
            const name = file.name || `${key}.png`;
            formData.append(`properties[${key}]`, file, name);
          }
        }

        if (sectionIds?.length) {
          formData.append('sections', sectionIds.join(','));
        }

        const response = await fetch(getCartAddUrl(), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        return response.json();
      },

      updateCartAttributes,
    };
  }

  /**
   * @param {{ provider: string, cloudName?: string, uploadPreset?: string, folder?: string }} config
   */
  function createUploader(config) {
    if (config.provider === PROVIDERS.CLOUDINARY) {
      return createCloudinaryUploader(config);
    }
    return createShopifyUploader();
  }

  /** Chaves do backup da personalização do pedido em cart.attributes (só URLs). */
  const PERS_BACKUP_KEYS = {
    saved: '_pers_saved',
    mockup: '_pers_mockup',
    logo: (i) => `_pers_logo_${i}`,
  };

  /** Property que identifica a linha de personalização no carrinho. */
  const PERS_LINE_PROP = '_embalagem_personalizada';

  /** Legacy — carrinhos salvos antes da renomeação. */
  const PERS_LINE_PROP_LEGACY = '_cart_packaging_personalization';

  function isPersLineItem(item) {
    const props = item?.properties || {};
    return props[PERS_LINE_PROP] === 'true' || props[PERS_LINE_PROP_LEGACY] === 'true';
  }

  /**
   * Properties mínimas da linha de embalagem (flag + Personalização + logos + mockup).
   * @param {{ logoUrls?: string[], mockupUrl?: string }} opts
   */
  function buildPersLineProperties({ logoUrls = [], mockupUrl = '' } = {}) {
    const properties = {
      [PERS_LINE_PROP]: 'true',
      Personalização: 'Sim',
    };
    logoUrls.forEach((url, i) => {
      if (url) properties[`_logo_${i + 1}`] = url;
    });
    if (mockupUrl) properties._mockup_preview = mockupUrl;
    return properties;
  }

  function buildPersBackupAttributes({ logoUrls = [], mockupUrl = '' }, maxLogos = 9) {
    const attrs = {
      [PERS_BACKUP_KEYS.saved]: logoUrls.length ? 'true' : '',
      [PERS_BACKUP_KEYS.mockup]: mockupUrl || '',
    };
    for (let i = 1; i <= maxLogos; i++) {
      attrs[PERS_BACKUP_KEYS.logo(i)] = logoUrls[i - 1] || '';
    }
    // Limpa chaves antigas do backup (brand/count/summary)
    attrs._pers_summary = '';
    attrs._pers_logo_count = '';
    attrs._pers_brand_label = '';
    return attrs;
  }

  function readPersBackup(attributes = {}, maxLogos = 9) {
    const logoUrls = [];
    for (let i = 1; i <= maxLogos; i++) {
      const url = attributes[PERS_BACKUP_KEYS.logo(i)];
      if (url) logoUrls.push(url);
    }
    return {
      saved: attributes[PERS_BACKUP_KEYS.saved] === 'true' && logoUrls.length > 0,
      mockupUrl: attributes[PERS_BACKUP_KEYS.mockup] || '',
      logoUrls,
    };
  }

  global.JvKitPersonalizationUpload = {
    createUploader,
    PROVIDERS,
    updateCartAttributes,
    PERS_BACKUP_KEYS,
    PERS_LINE_PROP,
    PERS_LINE_PROP_LEGACY,
    isPersLineItem,
    buildPersLineProperties,
    buildPersBackupAttributes,
    readPersBackup,
  };
})(typeof window !== 'undefined' ? window : globalThis);
